<template>
    <table class="results-table">
        <thead>
            <tr>
                <th class="col-pos"></th>
                <th class="team-header"></th>
                <th v-for="team in sortedTeams" :key="team.id" class="team-header">
                    <component
                        :is="teamPublicPath(team) ? 'a' : 'div'"
                        :href="teamPublicPath(team) || undefined"
                        class="header-team"
                        :class="{ 'header-team--link': !!teamPublicPath(team) }"
                    >
                        <div class="team-logo-small" v-if="team.logo_path">
                            <img :src="team.logo_path" :alt="team.name" />
                        </div>
                        <div class="team-logo-small placeholder" v-else>
                            {{ team.name?.charAt(0) }}
                        </div>
                        <span v-if="team.name" class="team-name-small">{{ team.name }}</span>
                    </component>
                </th>
                <th class="points-header">Победы</th>
                <th class="points-header">Очки</th>
                <th class="points-header">Сеты</th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="team in sortedTeams" :key="team.id">
                <td class="col-pos">
                    <span class="position" :class="{
                        'last-place': !isCupTable && getTeamPosition(team.id) === lastPosition,
                        'cup-second': isCupTable && !isPlayoffStage && getTeamPosition(team.id) === 2,
                        'cup-third-plus': isCupTable && (isPlayoffStage ? getTeamPosition(team.id) >= 2 : getTeamPosition(team.id) >= 3)
                    }" @mouseenter="handlePositionMouseEnter($event, getTeamPosition(team.id))"
                        @mouseleave="handlePositionMouseLeave">
                        {{ getTeamPosition(team.id) }}
                    </span>
                </td>
                <td class="team-row-header">
                    <component
                        :is="teamPublicPath(team) ? 'a' : 'div'"
                        :href="teamPublicPath(team) || undefined"
                        class="row-team"
                        :class="{ 'row-team--link': !!teamPublicPath(team) }"
                    >
                        <div class="team-logo-small" v-if="team.logo_path">
                            <img :src="team.logo_path" :alt="team.name" />
                        </div>
                        <div class="team-logo-small placeholder" v-else>
                            {{ team.name?.charAt(0) }}
                        </div>
                        <span v-if="team.name" class="team-name-small">{{ team.name }}</span>
                    </component>
                </td>
                <td v-for="opponent in sortedTeams" :key="opponent.id" class="result-cell"
                    :class="{ 'diagonal': team.id === opponent.id }">
                    <div v-if="team.id === opponent.id" class="diagonal-cell">
                        —
                    </div>
                    <div v-else class="match-results">
                        <!-- Для кубка (групповой этап) показываем все матчи в одной ячейке без разделения на домашний/выездной -->
                        <template v-if="isCupTable && !isPlayoffStage">
                            <div v-if="getCupMatchesForPair(team.id, opponent.id) && getCupMatchesForPair(team.id, opponent.id).length > 0"
                                class="match-results-group">
                                <div v-for="(match, index) in getCupMatchesForPair(team.id, opponent.id)"
                                    :key="`cup-${match.game?.id || index}`" class="match-result"
                                    :class="getSingleMatchResultClass(match)"
                                    @click="handleMatchClick($event, [match])">
                                    {{ getSingleResultDisplay(match) }}
                                </div>
                            </div>
                            <div v-else class="match-result result-upcoming no-match-data">
                                —
                            </div>
                        </template>
                        <!-- Для дополнительного этапа показываем один результат -->
                        <template v-else-if="isPlayoffStage">
                            <div v-if="getSingleMatchForPair(team.id, opponent.id)" class="match-result"
                                :class="getSingleMatchResultClass(getSingleMatchForPair(team.id, opponent.id))"
                                @click="handleMatchClick($event, [getSingleMatchForPair(team.id, opponent.id)])">
                                {{ getSingleResultDisplay(getSingleMatchForPair(team.id, opponent.id)) }}
                            </div>
                            <div v-else class="match-result result-upcoming no-match-data">
                                —
                            </div>
                        </template>
                        <!-- Для чемпионата (групповой этап) показываем две колонки (дома/в гостях) -->
                        <template v-else>
                            <!-- Матчи дома -->
                            <!-- Показываем все матчи пары в хронологическом порядке (раньше -> позже) -->
                            <div v-if="getCombinedMatches(team.id, opponent.id).length > 0" class="match-results-group">
                                <div v-for="(match, index) in getCombinedMatches(team.id, opponent.id)"
                                    :key="`match-${match.game?.id || index}`" class="match-result"
                                    :class="getSingleMatchResultClass(match)"
                                    @click="handleMatchClick($event, [match])">
                                    {{ getSingleResultDisplay(match) }}
                                </div>
                            </div>
                            <div v-else class="match-result result-upcoming no-match-data">
                                —
                            </div>
                        </template>
                    </div>
                </td>
                <td class="points-cell">
                    <span class="points-value">{{ getTeamWins(team.id) }}</span>
                </td>
                <td class="points-cell">
                    <span class="points-value">{{ getTeamPoints(team.id) }}</span>
                </td>
                <td class="points-cell sets-cell">
                    <div class="sets-score">
                        <span class="sets-won">{{ getTeamSets(team.id).setsWon }}</span>
                        <span class="sets-sep">:</span>
                        <span class="sets-lost">{{ getTeamSets(team.id).setsLost }}</span>
                    </div>
                    <div class="sets-ratio">({{ formatRatio(getTeamSets(team.id).setsWon, getTeamSets(team.id).setsLost)
                        }})</div>
                </td>
            </tr>
        </tbody>
    </table>

    <!-- Tooltip (вынесен в компонент) -->
    <MatchTooltip v-if="hoveredGames && hoveredGames.length > 0" :games="hoveredGames" :position="tooltipPosition"
        :positionClass="tooltipPositionClass" />

    <!-- Tooltip для позиций в кубке -->
    <div v-if="isCupTable && hoveredPosition" class="position-tooltip"
        :class="`tooltip-${positionTooltipPositionClass}`" :style="{
            left: positionTooltipPosition.x + 'px',
            top: positionTooltipPosition.y + 'px'
        }" @mouseenter="handlePositionTooltipMouseEnter" @mouseleave="handlePositionTooltipMouseLeave">
        <div class="position-tooltip-content">
            <span v-if="hoveredPosition === 1" class="position-tooltip-text">
                {{ isPlayoffStage ? 'Проход в финальный этап' : 'Проход в «Финал четырёх»' }}
            </span>
            <span v-else-if="hoveredPosition === 2 && !isPlayoffStage" class="position-tooltip-text">Дополнительный
                этап</span>
            <span v-else-if="(hoveredPosition === 2 && isPlayoffStage) || hoveredPosition === 3"
                class="position-tooltip-text">Вылет</span>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import MatchTooltip from '@/components/shared/MatchTooltip.vue';
import { formatSetsScore, formatRatio } from '@/utils/matchHelpers';
import { teamPublicPath } from '@/utils/teamUrl';

const props = defineProps({
    teams: {
        type: Array,
        default: () => []
    },
    games: {
        type: Array,
        default: () => []
    },
    isPlayoffStage: {
        type: Boolean,
        default: false
    },
    // Максимальное количество матчей для пары команд (по умолчанию 2: regular-1 и regular-2)
    maxMatchesPerPair: {
        type: Number,
        default: 2
    }
    ,
    // Сдвиг позиции (например, для таблицы 5-9 нужно +4)
    positionOffset: {
        type: Number,
        default: 0
    }
});

const hoveredGames = ref([]); // Массив матчей для tooltip
const tooltipPosition = ref({ x: 0, y: 0 });
const tooltipPositionClass = ref('top');
let hideTooltipTimeout = null;

// Tooltip для позиций в кубке
const hoveredPosition = ref(null);
const positionTooltipPosition = ref({ x: 0, y: 0 });
const positionTooltipPositionClass = ref('top');
let hidePositionTooltipTimeout = null;

// Проверяем, является ли это таблицей кубка (по количеству команд - в кубке по 3 команды в группе)
// Или по наличию матчей со stage === 'group_stage' и competition_type === 'cup'
const isCupTable = computed(() => {
    if (props.isPlayoffStage) {
        return false;
    }
    // Если 3 команды, это точно кубок (групповой этап)
    if (props.teams.length === 3) {
        return true;
    }
    // Проверяем по матчам - если есть матчи кубка группового этапа
    if (props.games && props.games.length > 0) {
        return props.games.some(game => game.stage === 'group_stage' && game.competition_type === 'cup');
    }
    return false;
});

// Упрощенная матрица для кубка (без разделения на home/away)
const cupMatchesMatrix = computed(() => {
    if (!isCupTable.value) {
        return {};
    }

    const matrix = {};

    // Инициализируем матрицу
    props.teams.forEach(team => {
        matrix[team.id] = {};
        props.teams.forEach(opponent => {
            if (team.id !== opponent.id) {
                matrix[team.id][opponent.id] = [];
            }
        });
    });

    // Группируем матчи по парам команд (только матчи кубка группового этапа)
    const matchesByPair = {};

    props.games.forEach(game => {
        // Фильтруем только матчи кубка группового этапа
        if (game.home_team?.id && game.away_team?.id &&
            game.stage === 'group_stage' && game.competition_type === 'cup') {
            const team1Id = Math.min(game.home_team.id, game.away_team.id);
            const team2Id = Math.max(game.home_team.id, game.away_team.id);
            const pairKey = `${team1Id}-${team2Id}`;

            if (!matchesByPair[pairKey]) {
                matchesByPair[pairKey] = [];
            }

            matchesByPair[pairKey].push(game);
        }
    });

        // Для каждой пары команд находим до maxMatchesPerPair матчей (тур 1, тур 2, затем 5-places)
    Object.keys(matchesByPair).forEach(pairKey => {
        const [minTeamId, maxTeamId] = pairKey.split('-').map(Number);
        const matches = matchesByPair[pairKey];

        // Дедуплицируем по ID матча
        const uniqueMatches = [];
        const seenGameIds = new Set();

        matches.forEach(game => {
            if (game.id && !seenGameIds.has(game.id)) {
                seenGameIds.add(game.id);
                uniqueMatches.push(game);
            }
        });

        // Сортируем уникальные матчи по дате (раньше -> позже)
        const extractDateForCup = (g) => {
            return g?.date || g?.match_date || g?.datetime || null;
        };
        uniqueMatches.sort((a, b) => {
            const dateAraw = extractDateForCup(a);
            const dateBraw = extractDateForCup(b);
            const dateA = dateAraw ? new Date(dateAraw.split?.('.') ? dateAraw.split('.').reverse().join('-') : dateAraw) : new Date(0);
            const dateB = dateBraw ? new Date(dateBraw.split?.('.') ? dateBraw.split('.').reverse().join('-') : dateBraw) : new Date(0);
            return dateA - dateB;
        });

        // Берем только первые N матчей (тур 1, тур 2, затем 5-places)
        const matchesToProcess = uniqueMatches.slice(0, props.maxMatchesPerPair);

        // Для каждого матча создаем данные с точки зрения каждой команды
        matchesToProcess.forEach((game) => {
            const actualHomeTeamId = game.home_team?.id;
            const actualAwayTeamId = game.away_team?.id;
            const homeScore = game.home_score || 0;
            const awayScore = game.away_score || 0;
            const isFinished = game.is_finished || (homeScore > 0 || awayScore > 0);

            // Обрабатываем матч для обеих команд в паре
            [minTeamId, maxTeamId].forEach(teamId => {
                const opponentId = teamId === minTeamId ? maxTeamId : minTeamId;
                const isTeamHome = actualHomeTeamId === teamId;

                // Счет с точки зрения команды в строке
                const teamScore = isTeamHome ? homeScore : awayScore;
                const opponentScore = isTeamHome ? awayScore : homeScore;
                const teamWon = isFinished && teamScore > opponentScore;

                const matchData = isFinished ? {
                    homeScore: teamScore,
                    awayScore: opponentScore,
                    sets: game.sets_score,
                    result: teamWon ? 'win' : 'loss',
                    game: game,
                    isFinished: true
                } : {
                    game: game,
                    isFinished: false
                };

                // Добавляем матч в матрицу (без разделения на home/away)
                if (matrix[teamId] && matrix[teamId][opponentId]) {
                    matrix[teamId][opponentId].push(matchData);
                }
            });
        });

                // Сортируем матчи по tour_number/стадии для каждой команды
        [minTeamId, maxTeamId].forEach(teamId => {
            const opponentId = teamId === minTeamId ? maxTeamId : minTeamId;
            if (matrix[teamId] && matrix[teamId][opponentId]) {
                // Сортируем по дате (раньше -> позже)
                matrix[teamId][opponentId].sort((a, b) => {
                    const dateAraw = a.game?.match_date || a.date || a.game?.date || a.game?.datetime || null;
                    const dateBraw = b.game?.match_date || b.date || b.game?.date || b.game?.datetime || null;
                    const dateA = dateAraw ? new Date(dateAraw.split?.('.') ? dateAraw.split('.').reverse().join('-') : dateAraw) : new Date(0);
                    const dateB = dateBraw ? new Date(dateBraw.split?.('.') ? dateBraw.split('.').reverse().join('-') : dateBraw) : new Date(0);
                    return dateA - dateB;
                });
            }
        });
    });

    return matrix;
});

// Создаем матрицу результатов
const resultsMatrix = computed(() => {
    const matrix = {};

    // Инициализируем матрицу
    props.teams.forEach(team => {
        matrix[team.id] = {};
        props.teams.forEach(opponent => {
            if (team.id !== opponent.id) {
                matrix[team.id][opponent.id] = {
                    home: [], // массив матчей дома
                    away: []  // массив матчей в гостях
                };
            }
        });
    });

    // Собираем все матчи между каждой парой команд
    const matchesByPair = {};
    const seenGameIds = new Set(); // Для дедупликации по ID матча

    props.games.forEach(game => {
        if (game.home_team?.id && game.away_team?.id) {
            // Пропускаем матчи кубка - они обрабатываются в cupMatchesMatrix
            if (isCupTable.value && game.stage === 'group_stage') {
                return;
            }

            // Пропускаем дубликаты по ID матча
            if (game.id && seenGameIds.has(game.id)) {
                return;
            }
            seenGameIds.add(game.id);

            const team1Id = game.home_team.id;
            const team2Id = game.away_team.id;

            // Создаем ключ для пары команд (упорядоченный по ID)
            const pairKey = `${Math.min(team1Id, team2Id)}-${Math.max(team1Id, team2Id)}`;

            if (!matchesByPair[pairKey]) {
                matchesByPair[pairKey] = [];
            }

            matchesByPair[pairKey].push(game);
        }
    });

    // Распределяем матчи по ячейкам
    Object.keys(matchesByPair).forEach(pairKey => {
        const [team1Id, team2Id] = pairKey.split('-').map(Number);
        const matches = matchesByPair[pairKey];

        // Проверяем, что обе команды есть в матрице
        if (matrix[team1Id] && matrix[team1Id][team2Id]) {
            // Дедупликация: убираем дубликаты по ID матча и по комбинации (stage, home_team, away_team)
            const uniqueMatches = [];
            const seenMatches = new Set();

            matches.forEach(game => {
                // Создаем уникальный ключ: stage + home_team_id + away_team_id
                const matchKey = `${game.stage || ''}-${game.home_team?.id || ''}-${game.away_team?.id || ''}-${game.id || ''}`;

                // Пропускаем дубликаты
                if (!seenMatches.has(matchKey)) {
                    seenMatches.add(matchKey);
                    uniqueMatches.push(game);
                }
            });

            const extractDateString = (item) => {
                const g = item.game || item;
                return g?.date || g?.match_date || g?.datetime || null;
            };

            const sortByDate = (arr) => {
                arr.sort((a, b) => {
                    const dateAraw = extractDateString(a);
                    const dateBraw = extractDateString(b);
                    const dateA = dateAraw ? new Date(dateAraw.split?.('.') ? dateAraw.split('.').reverse().join('-') : dateAraw) : new Date(0);
                    const dateB = dateBraw ? new Date(dateBraw.split?.('.') ? dateBraw.split('.').reverse().join('-') : dateBraw) : new Date(0);
                    return dateA - dateB;
                });
            };

            const distributeMatches = (matchesToProcess) => {
                matchesToProcess.forEach((game) => {
                    const isTeam1Home = game.home_team?.id === team1Id;
                    const homeScore = game.home_score;
                    const awayScore = game.away_score;
                    const isFinished = game.is_finished || (homeScore > 0 || awayScore > 0);
                    const team1Score = isTeam1Home ? homeScore : awayScore;
                    const team1OpponentScore = isTeam1Home ? awayScore : homeScore;
                    const team2Score = isTeam1Home ? awayScore : homeScore;
                    const team2OpponentScore = isTeam1Home ? homeScore : awayScore;
                    const team1Won = isFinished && team1Score > team1OpponentScore;
                    const team2Won = isFinished && team2Score > team2OpponentScore;

                    const matchDataForTeam1 = isFinished ? {
                        homeScore: team1Score,
                        awayScore: team1OpponentScore,
                        sets: game.sets_score,
                        result: team1Won ? 'win' : 'loss',
                        game: game,
                        isFinished: true
                    } : {
                        game: game,
                        isFinished: false
                    };

                    const matchDataForTeam2 = isFinished ? {
                        homeScore: team2Score,
                        awayScore: team2OpponentScore,
                        sets: game.sets_score,
                        result: team2Won ? 'win' : 'loss',
                        game: game,
                        isFinished: true
                    } : {
                        game: game,
                        isFinished: false
                    };

                    if (isTeam1Home) {
                        matrix[team1Id][team2Id].home.push(matchDataForTeam1);
                        matrix[team2Id][team1Id].away.push(matchDataForTeam2);
                    } else {
                        matrix[team1Id][team2Id].away.push(matchDataForTeam1);
                        matrix[team2Id][team1Id].home.push(matchDataForTeam2);
                    }
                });
            };

            const sortPairMatches = () => {
                const sortMatches = (matchList) => {
                    if (matchList.length === 0) return;
                    matchList.sort((a, b) => {
                        const dateAraw = extractDateString(a);
                        const dateBraw = extractDateString(b);
                        const dateA = dateAraw ? new Date(dateAraw.split('.').reverse().join('-')) : new Date(0);
                        const dateB = dateBraw ? new Date(dateBraw.split('.').reverse().join('-')) : new Date(0);
                        return dateA - dateB;
                    });
                };

                sortMatches(matrix[team1Id][team2Id].home);
                sortMatches(matrix[team1Id][team2Id].away);
                sortMatches(matrix[team2Id][team1Id].home);
                sortMatches(matrix[team2Id][team1Id].away);
            };

            // Дополнительный этап кубка: один матч на пару
            if (props.isPlayoffStage) {
                const playoffMatches = uniqueMatches.filter(m => m.stage === 'dop');
                sortByDate(playoffMatches);
                distributeMatches(playoffMatches.slice(0, 1));
                sortPairMatches();
                return;
            }

            // Выбираем матчи по категориям: regular (regular-1, regular-2) и 5-places.
            // Требование: если props.maxMatchesPerPair >= 4 - берем до 2 матчей из regular и до 2 матчей из 5-places.
            // Если в категории меньше матчей — оставляем пустые слоты (не дополняем из другой категории).
            const regularStages = ['regular-1', 'regular-2'];
            const regularMatches = uniqueMatches.filter(m => regularStages.includes(m.stage || (m.game && m.game.stage)));
            const fivePlaceMatches = uniqueMatches.filter(m => (m.stage || (m.game && m.game.stage)) === '5-places');

            sortByDate(regularMatches);
            sortByDate(fivePlaceMatches);

            // Debug logs to inspect selected matches and dates
            try {
                console.log('[ResultsTable] pair', pairKey, 'regularMatches', regularMatches.map(m => ({
                    id: m.id || (m.game && m.game.id),
                    stage: m.stage || (m.game && m.game.stage),
                    date: extractDateString(m)
                })), 'fivePlaceMatches', fivePlaceMatches.map(m => ({
                    id: m.id || (m.game && m.game.id),
                    stage: m.stage || (m.game && m.game.stage),
                    date: extractDateString(m)
                })));
            } catch (e) {
                console.log('[ResultsTable] debug log error', e);
            }

            // Выбираем до 2 матчей из каждой категории
            const selectedRegular = regularMatches.slice(0, 2);
            const selectedFive = fivePlaceMatches.slice(0, 2);

            // Объединяем выбранные матчи и затем сортируем по дате
            let combined = [...selectedRegular, ...selectedFive];
            sortByDate(combined);
            try {
                console.log('[ResultsTable] pair', pairKey, 'selectedRegular', selectedRegular.map(m => ({
                    id: m.id || (m.game && m.game.id),
                    date: extractDateString(m)
                })), 'selectedFive', selectedFive.map(m => ({
                    id: m.id || (m.game && m.game.id),
                    date: extractDateString(m)
                })), 'combined', combined.map(m => ({
                    id: m.id || (m.game && m.game.id),
                    stage: m.stage || (m.game && m.game.stage),
                    date: extractDateString(m)
                })));
            } catch (e) {
                console.log('[ResultsTable] debug log error', e);
            }

            // Если props.maxMatchesPerPair < combined.length, обрезаем справа
            const matchesToProcess = combined.slice(0, props.maxMatchesPerPair);
            try {
                console.log('[ResultsTable] pair', pairKey, 'matchesToProcess', matchesToProcess.map(m => ({
                    id: m.id || (m.game && m.game.id),
                    stage: m.stage || (m.game && m.game.stage),
                    date: extractDateString(m)
                })));
            } catch (e) {
                console.log('[ResultsTable] debug log error', e);
            }

            distributeMatches(matchesToProcess);
            sortPairMatches();
        }
    });

    return matrix;
});

// Подсчет побед для команды
const getTeamWins = (teamId) => {
    let wins = 0;
    const teamGames = props.games.filter(g =>
        (g.home_team?.id === teamId || g.away_team?.id === teamId) && g.is_finished
    );

    teamGames.forEach(game => {
        if (game.home_team?.id === teamId) {
            // Команда играла дома
            if (game.home_score > game.away_score) {
                wins++;
            }
        } else {
            // Команда играла в гостях
            if (game.away_score > game.home_score) {
                wins++;
            }
        }
    });

    return wins;
};

// Подсчет очков для команды
const getTeamPoints = (teamId) => {
    let points = 0;
    const teamGames = props.games.filter(g =>
        (g.home_team?.id === teamId || g.away_team?.id === teamId) && g.is_finished
    );

    teamGames.forEach(game => {
        if (game.home_team?.id === teamId) {
            // Команда играла дома
            // home_score и away_score - это количество выигранных сетов
            if (game.home_score > game.away_score) {
                // Победа: 3:2 = 2 очка, 3:0/3:1 = 3 очка
                points += game.away_score === 2 ? 2 : 3;
            } else {
                // Поражение: 2:3 = 1 очко, 0:3/1:3 = 0 очков
                points += game.home_score === 2 ? 1 : 0;
            }
        } else {
            // Команда играла в гостях
            if (game.away_score > game.home_score) {
                // Победа: 3:2 = 2 очка, 3:0/3:1 = 3 очка
                points += game.home_score === 2 ? 2 : 3;
            } else {
                // Поражение: 2:3 = 1 очко, 0:3/1:3 = 0 очков
                points += game.away_score === 2 ? 1 : 0;
            }
        }
    });

    return points;
};

// Подсчет выигранных и проигранных сетов для команды
const getTeamSets = (teamId) => {
    let setsWon = 0;
    let setsLost = 0;
    const teamGames = props.games.filter(g =>
        (g.home_team?.id === teamId || g.away_team?.id === teamId) && g.is_finished
    );

    teamGames.forEach(game => {
        if (game.home_team?.id === teamId) {
            // Команда играла дома
            setsWon += game.home_score || 0;
            setsLost += game.away_score || 0;
        } else {
            // Команда играла в гостях
            setsWon += game.away_score || 0;
            setsLost += game.home_score || 0;
        }
    });

    return { setsWon, setsLost };
};

// Отсортированные команды: по победам, затем по очкам, затем по отношению сетов
const sortedTeams = computed(() => {
    return [...props.teams].sort((a, b) => {
        const winsA = getTeamWins(a.id);
        const winsB = getTeamWins(b.id);
        const pointsA = getTeamPoints(a.id);
        const pointsB = getTeamPoints(b.id);

        // 1. Сначала сравниваем по победам
        if (winsA !== winsB) {
            return winsB - winsA; // больше побед - выше
        }

        // 2. При равном количестве побед - по очкам
        if (pointsA !== pointsB) {
            return pointsB - pointsA; // больше очков - выше
        }

        // 3. При равных очках - по соотношению сетов (выигранные / проигранные)
        const setsA = getTeamSets(a.id);
        const setsB = getTeamSets(b.id);

        const ratioA = setsA.setsLost > 0
            ? setsA.setsWon / setsA.setsLost
            : (setsA.setsWon > 0 ? 999 : 0);

        const ratioB = setsB.setsLost > 0
            ? setsB.setsWon / setsB.setsLost
            : (setsB.setsWon > 0 ? 999 : 0);

        // Сравниваем float-значения
        if (ratioA > ratioB) return -1;
        if (ratioA < ratioB) return 1;
        return 0;
    });
});

// Получение позиции команды в таблице (начинается с 1)
const getTeamPosition = (teamId) => {
    return sortedTeams.value.findIndex(team => team.id === teamId) + 1 + (props.positionOffset || 0);
};

const lastPosition = computed(() => {
    if (!sortedTeams.value.length) {
        return null;
    }

    return sortedTeams.value.length + (props.positionOffset || 0);
});

// using shared helpers (imported above)

// Получение результата для отображения
// В волейболе homeScore и awayScore - это количество выигранных сетов (3:0, 3:1, 3:2)
// Получаем классы для одного матча
const getSingleMatchResultClass = (match) => {
    if (!match) {
        return 'result-upcoming no-match-data';
    }

    if (match.isFinished) {
        const noStats = match.game && !match.game.has_stats ? ' result-no-stats' : '';
        return `result-${match.result}${noStats}`;
    }
    return 'result-upcoming';
};

// Получаем отображение результата для одного матча
const getSingleResultDisplay = (match) => {
    if (!match) {
        return '—';
    }

    if (!match.isFinished) {
        // Для несыгранных матчей показываем дату
        if (match.game && match.game.datetime) {
            const dateStr = match.game.datetime.split(' ')[0]; // берем только дату
            return dateStr;
        }
        return '—';
    }

    // Показываем счет по сетам (например, 3:0, 3:1, 3:2)
    return `${match.homeScore}:${match.awayScore}`;
};

// Получить один матч для пары команд (для дополнительного этапа)
// Результат должен быть с точки зрения teamId
const getSingleMatchForPair = (teamId, opponentId) => {
    // Ищем матч в home или away массивах
    const homeMatch = resultsMatrix.value[teamId]?.[opponentId]?.home?.[0];
    const awayMatch = resultsMatrix.value[teamId]?.[opponentId]?.away?.[0];

    // Если матч найден в home, возвращаем его как есть
    if (homeMatch) {
        return homeMatch;
    }

    // Если матч найден в away, возвращаем его (результат уже инвертирован в матрице)
    if (awayMatch) {
        return awayMatch;
    }

    return null;
};

// Возвращает объединённый список матчей (home + away) для пары, отсортированный по дате (раньше -> позже)
const getCombinedMatches = (teamId, opponentId) => {
    const home = resultsMatrix.value[teamId]?.[opponentId]?.home || [];
    const away = resultsMatrix.value[teamId]?.[opponentId]?.away || [];
    const combined = [...home, ...away];

    const extractDate = (item) => {
        const g = item.game || item;
        return g?.match_date || g?.date || g?.datetime || null;
    };

    combined.sort((a, b) => {
        const dateAraw = extractDate(a);
        const dateBraw = extractDate(b);
        const dateA = dateAraw ? new Date(dateAraw.split?.('.') ? dateAraw.split('.').reverse().join('-') : dateAraw) : new Date(0);
        const dateB = dateBraw ? new Date(dateBraw.split?.('.') ? dateBraw.split('.').reverse().join('-') : dateBraw) : new Date(0);
        return dateA - dateB;
    });

    return combined;
};

// Получить все матчи кубка для пары команд
// Для кубка нет разделения на домашний/выездной - все матчи в одной ячейке
// Тур 1 - выше, тур 2 - ниже
const getCupMatchesForPair = (teamId, opponentId) => {
    // Используем упрощенную матрицу для кубка
    const matches = cupMatchesMatrix.value[teamId]?.[opponentId] || [];

    // Матчи уже отсортированы по tour_number и ограничены до 2
    return matches;
};


// Открывает tooltip по клику на элемент(ы)
const handleMatchClick = (event, matches) => {
    if (!matches || !Array.isArray(matches) || matches.length === 0) return;

    hoveredGames.value = matches.map(m => m.game).filter(g => g);

    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 350;
    const tooltipHeight = hoveredGames.value.length > 1 ? 100 + (hoveredGames.value.length * 120) : 300;

    let x = rect.left + rect.width / 2;
    let y = rect.top - 10;
    let positionClass = 'top';

    if (x - tooltipWidth / 2 < 10) {
        x = tooltipWidth / 2 + 10;
    } else if (x + tooltipWidth / 2 > window.innerWidth - 10) {
        x = window.innerWidth - tooltipWidth / 2 - 10;
    }

    if (y - tooltipHeight < 10) {
        y = rect.bottom + 10;
        positionClass = 'bottom';
    }

    tooltipPosition.value = { x, y };
    tooltipPositionClass.value = positionClass;
};

const onDocumentClick = (e) => {
    const t = e.target;
    if (t.closest && (t.closest('.match-tooltip') || t.closest('.match-result') || t.closest('.match-square'))) {
        return;
    }
    hoveredGames.value = [];
};

onMounted(() => {
    document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick);
});

// Обработка наведения на позицию в кубке
const handlePositionMouseEnter = (event, position) => {
    if (!isCupTable.value || position > 3) {
        return;
    }

    // Отменяем таймер скрытия, если он есть
    if (hidePositionTooltipTimeout) {
        clearTimeout(hidePositionTooltipTimeout);
        hidePositionTooltipTimeout = null;
    }

    hoveredPosition.value = position;
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 200;
    const tooltipHeight = 50;

    let x = rect.left + rect.width / 2;
    let y = rect.top - 10;
    let positionClass = 'top';

    // Проверяем границы экрана по горизонтали
    if (x - tooltipWidth / 2 < 10) {
        x = tooltipWidth / 2 + 10;
    } else if (x + tooltipWidth / 2 > window.innerWidth - 10) {
        x = window.innerWidth - tooltipWidth / 2 - 10;
    }

    // Проверяем границы экрана по вертикали
    if (y - tooltipHeight < 10) {
        y = rect.bottom + 10;
        positionClass = 'bottom';
    }

    positionTooltipPosition.value = { x, y };
    positionTooltipPositionClass.value = positionClass;
};

const handlePositionMouseLeave = () => {
    // Добавляем небольшую задержку перед скрытием
    hidePositionTooltipTimeout = setTimeout(() => {
        hoveredPosition.value = null;
    }, 100);
};

const handlePositionTooltipMouseEnter = () => {
    // Отменяем скрытие tooltip, если курсор на tooltip
    if (hidePositionTooltipTimeout) {
        clearTimeout(hidePositionTooltipTimeout);
        hidePositionTooltipTimeout = null;
    }
};

const handlePositionTooltipMouseLeave = () => {
    // Скрываем tooltip при уходе курсора с tooltip
    hoveredPosition.value = null;
};
</script>

<style scoped>
.match-results-group {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.match-results-group .match-result {
    display: block;
}

/* Стили для позиций в кубке */
.position {
    cursor: pointer;
    transition: opacity 0.2s ease;
}

.position:hover {
    opacity: 0.8;
}

/* Позиция 2 в кубке - синий фон (info цвет) */
.position.cup-second {
    background: #3b82f6 !important;
    color: #fff !important;
}

/* Позиция 3 и далее в кубке - красный фон (как last-place) */
.position.cup-third-plus {
    background: #ef4444 !important;
    color: #fff !important;
}

/* Tooltip для позиций в кубке */
.position-tooltip {
    position: fixed;
    z-index: 1001;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1);
    padding: 0.75rem 1rem;
    min-width: 180px;
    pointer-events: auto;
    animation: tooltipFadeIn 0.2s ease-out;
    border: 1px solid #e2e8f0;
}

.position-tooltip.tooltip-top {
    transform: translate(-50%, -100%);
    margin-top: -8px;
}

.position-tooltip.tooltip-bottom {
    transform: translate(-50%, 0);
    margin-top: 8px;
}

.position-tooltip-content {
    display: flex;
    align-items: center;
    justify-content: center;
}

.position-tooltip-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1e293b;
    text-align: center;
}

@keyframes tooltipFadeIn {
    from {
        opacity: 0;
        transform: translate(-50%, -90%);
    }

    to {
        opacity: 1;
        transform: translate(-50%, -100%);
    }
}

.sets-score {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.sets-won {
    color: #10b981;
    font-weight: 600;
}

.sets-lost {
    color: #ef4444;
    font-weight: 600;
}

.sets-ratio {
    color: #000;
    font-size: 0.75rem;
    margin-top: 4px;
    display: block;
    text-align: center;
}
</style>
